<script setup lang='ts'>
import { Head, Link, router } from '@inertiajs/vue3'
import Button from '@/Components/Utils/Button.vue'
import MultiSelect from 'primevue/multiselect'
import Dialog from 'primevue/dialog'
import { useFormatTime } from '@/Composables/useFormatTime'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { inject, ref } from 'vue'
import axios from 'axios'
import { notify } from '@kyvg/vue3-notification'
import { useTruncate } from '@/Composables/useTruncate'
import LoadingIcon from '@/Components/Utils/LoadingIcon.vue'
import Tag from '@/Components/Utils/Tag.vue'
import { routeType } from '@/types/route'
import { aikuLocaleStructure } from '@/Composables/useLocaleStructure'
import Icon from '@/Components/Utils/Icon.vue'
import { DatePicker } from 'primevue'
import CountUp from 'vue-countup-v3'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faFilePdf, faTimes, faArrowRight, faArrowLeft, faPencil, faTrashUndo, faCalendarAlt, faDollarSign } from '@fal'
import { faBadgePercent, faTag } from '@fas'
import { library } from '@fortawesome/fontawesome-svg-core'
import { InvoiceResource, OrdersResource } from '@/types/Resources'
import OrderInInvoiceMaster from '@/Components/Master/Invoices/OrderInInvoiceMaster.vue'
library.add(faFilePdf, faTimes, faArrowRight, faArrowLeft, faPencil, faTrashUndo, faCalendarAlt, faDollarSign, faTag, faBadgePercent)

const props = defineProps<{
    invoice: InvoiceResource
    invoiceCross?: InvoiceResource
    updateOrderRoute: routeType
    resetOrderRoute: routeType
}>()

// const locale = inject('locale', aikuLocaleStructure)
const selectedInvoice = ref<InvoiceResource | null>(null)

// Fetch Order
const selectedOrderId = ref<number[]>([])
const isLoadingOrder = ref(false)
const listOrder = ref([])
const fetchOrder = async () => {
    isLoadingOrder.value = true
    try {
        const { data } = await axios.get(route('json.orders.status', ['unpaid', props.invoice?.sales?.id]))
        console.log('data order', data)
        listOrder.value = data
        return data
    } catch (error) {
        console.error('error', error)
        notify({
            title: 'Oops!',
            text: 'Gagal dalam mencari data Order',
            type: 'error',
        })
    } finally {
        isLoadingOrder.value = false
    }
}

// Section: Add order
const isModalOrderList = ref(false)
const onAddOrder = (idInvoice?: number) => {
    console.log('idinvoice', idInvoice)
    if (!idInvoice) {
        notify({
            title: 'Pilih invoice terlebih dahulu',
            text: '',
            type: 'error',
        })

        return
    }
    router.patch(
        route('master.invoices.add_order', [idInvoice]),
        {
            // invoice_id: props.invoice.id,
            orders: selectedOrderId.value
        },
        {
            preserveState: true,
            preserveScroll: true,
            onStart: () => isLoadingOrder.value = true,
            onFinish: () => isLoadingOrder.value = false,
            onSuccess: () => {
                isModalOrderList.value = false
            },
            onError: () => notify({
                title: 'Oops!',
                text: 'Gagal menambah order',
                type: 'error',
            }),
        }
    )
}



// Section: set to Paid
const isLoadingPaid = ref<number | null>(null)
const setToPaid = (idInvoice: number) => {
    console.log('idinvoice', idInvoice)
    if (!idInvoice) {
        notify({
            title: 'Pilih invoice terlebih dahulu',
            text: '',
            type: 'error',
        })

        return
    }

    router.patch(
        route('master.invoices.to_paid', [idInvoice]),
        {
            // invoice_id: props.invoice.id
        },
        {
            preserveState: true,
            preserveScroll: true,
            onStart: () => isLoadingPaid.value = idInvoice,
            onFinish: () => isLoadingPaid.value = null,
            onSuccess: () => {
                notify({
                    title: 'Success!',
                    text: 'Invoice telah dibayar',
                    type: 'success',
                })
            },
            onError: (e) => {
                console.error('error', e)
                notify({
                    title: 'Oops!',
                    text: 'Gagal melunaskan invoice.',
                    type: 'error',
                })
            },
        }
    )
}

// Section: set to unpaid
const isLoadingUnpaid = ref<number | null>(null)
const setToUnpaid = (idInvoice: number) => {
    console.log('idinvoice', idInvoice)
    if (!idInvoice) {
        notify({
            title: 'Pilih invoice terlebih dahulu',
            text: '',
            type: 'error',
        })

        return
    }

    router.patch(
        route('master.invoices.to_unpaid', [idInvoice]),
        {
            // invoice_id: props.invoice.id
        },
        {
            preserveState: true,
            preserveScroll: true,
            onStart: () => isLoadingUnpaid.value = idInvoice,
            onFinish: () => isLoadingUnpaid.value = null,
            onSuccess: () => {
                notify({
                    title: 'Success!',
                    text: 'Berhasil membatalkan pelunasan',
                    type: 'info',
                })
            },
            onError: (e) => {
                console.error('error', e)
                notify({
                    title: 'Oops!',
                    text: 'Gagal melunaskan invoice.',
                    type: 'error',
                })
            },
        }
    )
}


// Section: Export
const isLoadingExport = ref<number | null>(null);
const downloadPDF = async (idInvoice: number, fileName: string) => {
    try {
        isLoadingExport.value = idInvoice;

        // Fetch the PDF file (or trigger the server to generate it)
        const response = await axios.get(route('master.invoices.pdf', { id: idInvoice }), {
        responseType: 'blob', // Ensures the response is treated as a file
        });

        // Create a blob URL and trigger download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
    } catch (error) {
        console.error('Error downloading PDF:', error);
    } finally {
        isLoadingExport.value = null;
    }
}



// Section: Edit Order
const isLoadingEditOrder = ref<number | null | undefined>(null)
const isModalEditOrder = ref(false)
const selectedEditOrder = ref<OrdersResource | null>(null)
const onEditOrder = () => {
    console.log('selectedEditOrder', selectedEditOrder.value)
    if (selectedEditOrder.value) {
        router[props.updateOrderRoute?.method || 'patch'](
            route(props.updateOrderRoute.name, [props.invoice.id, selectedEditOrder.value?.id]),
            {
                product_price_in_invoice: selectedEditOrder.value?.product_price_show,
                quantity_in_invoice: selectedEditOrder.value?.quantity_show,
                discount_in_percentage: selectedEditOrder.value?.discount_in_percentage,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onStart: () => isLoadingEditOrder.value = selectedEditOrder.value?.id,
                onFinish: () => isLoadingEditOrder.value = null,
                onSuccess: () => {
                    isModalEditOrder.value = false
                },
                onError: () => notify({
                    title: 'Oops!',
                    text: 'Gagal mengedit order',
                    type: 'error',
                }),
            }
        )
    } else {
        notify({
            title: 'Oops!',
            text: 'Order belum dipilih',
            type: 'error',
        })
    }
}
const onResetOrder = () => {
    if (selectedEditOrder.value) {
        // selectedEditOrder.value.quantity_show = selectedEditOrder.value.quantity
        // selectedEditOrder.value.product_price_show = selectedEditOrder.value?.product?.price

        router[props.resetOrderRoute?.method || 'patch'](
            route(props.resetOrderRoute.name, [props.invoice.id, selectedEditOrder.value?.id]),
            {
                // product_price_in_invoice: selectedEditOrder.value?.product_price_show,
                // quantity_in_invoice: selectedEditOrder.value?.quantity_show,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onStart: () => isLoadingEditOrder.value = selectedEditOrder.value?.id,
                onFinish: () => isLoadingEditOrder.value = null,
                onSuccess: () => {
                    isModalEditOrder.value = false
                },
                onError: () => notify({
                    title: 'Oops!',
                    text: 'Gagal mereset diskon pada order',
                    type: 'error',
                }),
            }
        )
    } else {
        notify({
            title: 'Oops!',
            text: 'Order belum dipilih',
            type: 'error',
        })
    }
}


// Section: set to unpaid
const loadingInvoiceDueDate = ref<number[]>([])
const updateInvoiceDueDate = (idInvoice: number, due_date: Date) => {
    console.log('idinvoice', idInvoice)
    if (!idInvoice) {
        notify({
            title: 'Pilih invoice terlebih dahulu',
            text: '',
            type: 'error',
        })

        return
    }

    router.patch(
        route('master.invoices.update', [idInvoice]),
        {
            // Convert to Jakarta timezone (UTC+7) before formatting
            due_date: (() => {
                const date = new Date(due_date);
                // Jakarta is UTC+7
                const jakartaOffset = 7 * 60; // minutes
                // get UTC time in ms, add offset in ms
                const jakartaDate = new Date(date.getTime() + (jakartaOffset - date.getTimezoneOffset()) * 60000);
                return jakartaDate.toISOString().split('T')[0];
            })(),
        },
        {
            preserveState: true,
            preserveScroll: true,
            onStart: () => loadingInvoiceDueDate.value.push(idInvoice),
            onFinish: () => loadingInvoiceDueDate.value = loadingInvoiceDueDate.value.filter(id => id !== idInvoice),
            onSuccess: () => {
                notify({
                    title: 'Success!',
                    text: 'Berhasil mengubah tanggal jatuh tempo',
                    type: 'info',
                })
            },
            onError: (e) => {
                console.error('error', e)
                notify({
                    title: 'Oops!',
                    text: 'Gagal mengubah tanggal jatuh tempo.',
                    type: 'error',
                })
            },
        }
    )
}
</script>

<template>
    <section aria-labelledby="summary-heading" class="pt-8 xl:pt-0 flex-grow max-w-4xl pb-10 px-0 text-sm tabular-nums">
        <h2 id="summary-heading" class="text-xl text-center font-bold py-2 mb-2 border-b border-gray-300" :class="invoice.billing_date ? 'bg-green-200' : 'bg-gray-100'">
            {{ invoice.type_label }}
            <FontAwesomeIcon v-if="invoice.billing_date" v-tooltip="`Sudah lunas`" icon='fal fa-check' class='text-green-500' fixed-width aria-hidden='true' />
            <Button v-if="invoice.billing_date" @click="() => setToUnpaid(invoice.id)" label="Batal lunas" type="negative" :loading="isLoadingUnpaid === invoice.id" />
        </h2>

        <div class="flex justify-between flex-col lg:flex-row gap-y-3">
            <div>
                <label for="datepicker-12h" class="font-bold block mb-1">Jatuh tempo </label>
                <DatePicker
                    v-if="!invoice.billing_date"
                    id="datepicker-12h"
                    :modelValue="new Date(invoice.due_date)"
                    @update:modelValue="(e) => (invoice.due_date = e, updateInvoiceDueDate(invoice.id, e))"
                    fluid
                    :minDate="new Date()"
                    locale="id-ID"
                    dateFormat="dd M yy"
                    :disabled="loadingInvoiceDueDate.includes(invoice.id)"
                />
                
                <div v-else class="text-base whitespace-nowrap">{{ useFormatTime(invoice.due_date) }}</div>
            </div>
            <div class="self-end flex flex-wrap w-full justify-end gap-x-2 gap-y-1">
                <Button
                    v-if="!invoice.billing_date"
                    @click="() => setToPaid(invoice.id)"
                    label="Lunaskan"
                    :loading="isLoadingPaid == invoice.id"
                    size="l"
                    class="flex-grow lg:flex-grow-0"
                />

                <!-- <a :href="route('master.invoices.pdf', invoice.id)" download> -->
                    <Button
                        @click="() => downloadPDF(invoice.id, invoice.reference)"
                        label="Export to PDF"
                        :style="'tertiary'"
                        icon="fal fa-file-pdf"
                        size="l"
                        :loading="isLoadingExport == invoice.id"
                    />
                <!-- </a> -->
            </div>
        </div>

        <div class="mt-3">
            <TransitionGroup tag="ul" name="list"  class="border border-gray-200 border-l-0 font-medium">
                <template 
                    v-for="order, idxOrder in invoice.orders"
                    :key="order.id"
                >
                    <OrderInInvoiceMaster
                        :index="idxOrder"
                        :order
                        :isLoadingEditOrder
                        :invoice
                        :invoiceCross
                        :selectedEditOrder="selectedEditOrder"
                        :isModalEditOrder="isModalEditOrder"
                        @selectedEditOrder="(e: OrdersResource) => (selectedEditOrder = e)"
                        @isModalEditOrder="(e) => (isModalEditOrder = e)"
                    />
                </template>
            </TransitionGroup>

            <!-- Button: tambah order -->
            <div class="my-4">
                <Button v-if="!invoice.billing_date" @click="() => (isModalOrderList = true, selectedInvoice = invoice)" full :style="'dashed'" label="Tambah order" icon="fal fa-plus" />
            </div>

            <!-- Section: Total -->
            <dl class="flex items-center justify-between py-3 text-lg font-medium px-3 border-t border-dashed border-gray-300">
                <dt class="">Total</dt>
                <dd class="font-bold tabular-nums">
                    <CountUp :endVal="invoice.final_total || 0" :duration="1.2" :scrollSpyOnce="true"
                        :options="{
                        formattingFn: (number: number) => useFormatCurrency(number)
                    }" />
                </dd>
            </dl>
        </div>

        <!-- Modal: add Order -->
        <Dialog
            v-model:visible="isModalOrderList"
            @show="() => (selectedOrderId = [], fetchOrder())"
            modal
            header="Buat Invoice"
            :style="{ width: '30rem' }"
            xdismissableMask
        >   
            <div class="grid items-center gap-2 mb-4">
                <label for="username" class="font-medium w-24">Order</label>
                <MultiSelect
                    v-model="selectedOrderId"
                    :options="listOrder"
                    optionLabel="reference"
                    optionValue="id"
                    :disabled="isLoadingOrder"
                    :loading="isLoadingOrder"
                    filter
                    filterMatchMode="contains"
                    :filterFields="['reference', 'product.name', 'brand.name', 'sales.name']"
                    placeholder="Pilih order"
                    :maxSelectedLabels="3"
                    class="w-full lg:w-80"
                >
                    <template #option="slotProps">
                        <div class="grid">
                            <div class="font-semibold">
                                {{ slotProps.option?.reference }}
                            </div>
                            <div class="text-gray-500">
                                {{ useFormatTime(slotProps.option?.created_at) }}
                                <span v-if="slotProps.option?.brand?.name">
                                    | <FontAwesomeIcon icon='fal fa-copyright' class='text-gray-400' fixed-width aria-hidden='true' /> {{ useTruncate(slotProps.option?.brand?.name, 15) }}
                                </span>
                                <span>
                                    | <Icon :data="slotProps.option?.product.type_icon" />{{ useTruncate(slotProps.option?.product?.name, 15) }}
                                </span>
                            </div>
                        </div>
                    </template>
                </MultiSelect>
            </div>
            
            <div class="flex justify-end gap-2">
                <Button label="Batal" :style="'tertiary'" @click="isModalOrderList = false"></Button>
                <Button label="Pilih" :disabled="!selectedOrderId.length" :loading="isLoadingOrder" full @click="onAddOrder(selectedInvoice?.id)"></Button>
            </div>
        </Dialog>

        <!-- Modal: edit order -->
        <Dialog
            v-model:visible="isModalEditOrder"
            @after-hide="() => selectedEditOrder = null"
            modal
            :header="`Edit Order ${selectedEditOrder?.reference}`"
            :style="{ width: '30rem' }"
            dismissableMask
        >   
            <div v-if="selectedEditOrder" class="pt-1">
                <Button
                    label="Reset modifikasi"
                    type="negative"
                    v-tooltip="'Reset kuantitas, harga, dan total. Tidak mereset persentase diskon.'"
                    icon="fal fa-trash-undo"
                    :disabled="!selectedEditOrder.is_have_discount"
                    :loading="isLoadingEditOrder === selectedEditOrder?.id"
                    full
                    @click="async () => (onResetOrder())"
                />

                <!-- Kuantitas -->
                <div class="mt-6 grid items-center gap-2 mb-4">
                    <label for="username" class="font-medium">
                        Kuantitas
                        <span class="text-gray-400 font-normal">(original: {{ selectedEditOrder.quantity_by_type }})</span>
                    </label>
                    <input
                        v-model="selectedEditOrder.quantity_show"
                        @change="() => delete errors?.quantity_show"
                        type="tel"
                        pattern="[0-9]*"
                        inputmode="numeric"
                        name="input-price"
                        :disabled="isLoadingEditOrder === selectedEditOrder?.id"
                        id="input-price"
                        class="block w-full rounded-md bg-white px-3 py-1.5 text-base placeholder:text-gray-400 focus:ring-sky-300 disabled:bg-gray-200 disabled:text-gray-500 disabled:border-gray-400 sm:text-sm/6"
                        placeholder="999 pcs"
                        aria-describedby="input-price"
                    />
                    <div v-if="selectedEditOrder.manufacture?.finishing_count"
                        @click="() => selectedEditOrder.quantity_show = selectedEditOrder.manufacture?.finishing_count"
                        class="-mt-1 text-xs italic"
                        :class="[selectedEditOrder.quantity_show === selectedEditOrder.manufacture?.finishing_count ? 'text-gray-400' : 'hover:underline cursor-pointer']"
                    >
                        Sesuaikan dengan jumlah di Manufacture Finishing ({{ selectedEditOrder.manufacture?.finishing_count }})
                    </div>

                    <div v-if="selectedEditOrder.manufacture?.validated_count"
                        @click="() => selectedEditOrder.quantity_show = selectedEditOrder.manufacture?.validated_count"
                        class="-mt-1 text-xs italic"
                        :class="[selectedEditOrder.quantity_show === selectedEditOrder.manufacture?.validated_count ? 'text-gray-400' : 'hover:underline cursor-pointer']"
                    >
                        Sesuaikan dengan jumlah di Manufacture Validasi ({{ selectedEditOrder.manufacture?.validated_count }})
                    </div>

                    <div v-if="selectedEditOrder.manufacture?.count_validate_from_customer"
                        @click="() => selectedEditOrder.quantity_show = selectedEditOrder.manufacture?.count_validate_from_customer"
                        class="-mt-1 text-xs italic"
                        :class="[selectedEditOrder.quantity_show === selectedEditOrder.manufacture?.count_validate_from_customer ? 'text-gray-400' : 'hover:underline cursor-pointer']"
                    >
                        Sesuaikan dengan validasi dari customer ({{ selectedEditOrder.manufacture?.count_validate_from_customer }})
                    </div>
                </div>

                <!-- Harga -->
                <div class="grid items-center gap-2 mb-4">
                    <label for="username" class="font-medium">
                        Harga
                        <span class="text-gray-400 font-normal">(original: {{ selectedEditOrder.product_price }})</span>
                    </label>
                    <input
                        v-model="selectedEditOrder.product_price_show"
                        @change="() => delete errors?.product_price_show"
                        type="tel"
                        pattern="[0-9]*"
                        inputmode="numeric"
                        name="input-price"
                        :disabled="isLoadingEditOrder === selectedEditOrder?.id"
                        id="input-price"
                        class="block w-full rounded-md bg-white px-3 py-1.5 text-base placeholder:text-gray-400 focus:ring-sky-300 disabled:bg-gray-200 disabled:text-gray-500 disabled:border-gray-400 sm:text-sm/6"
                        placeholder="999 pcs"
                        aria-describedby="input-price"
                    />
                </div>

                <!-- Persentase Diskon -->
                <div class="grid items-center gap-2 mb-4">
                    <label for="username" class="font-medium">Persentase Diskon</label>
                    <input
                        v-model="selectedEditOrder.discount_in_percentage"
                        @change="() => delete errors?.discount_in_percentage"
                        type="tel"
                        pattern="[0-9]*"
                        inputmode="numeric"
                        name="input-percentage-discount"
                        :disabled="isLoadingEditOrder === selectedEditOrder?.id"
                        id="input-percentage-discount"
                        class="block w-full rounded-md bg-white px-3 py-1.5 text-base placeholder:text-gray-400 focus:ring-sky-300 disabled:bg-gray-200 disabled:text-gray-500 disabled:border-gray-400 sm:text-sm/6"
                        placeholder="999 pcs"
                        aria-describedby="input-percentage-discount"
                    />
                    <div class="flex gap-x-1 -mt-1">
                        <Button @click="selectedEditOrder.discount_in_percentage = 0" :key="`0${selectedEditOrder.discount_in_percentage === 0}`" :type="selectedEditOrder.discount_in_percentage === 0 ? 'indigo' : 'tertiary'" label="Tanpa diskon" size="xxs" />
                        <Button @click="selectedEditOrder.discount_in_percentage = 5" :key="`5${selectedEditOrder.discount_in_percentage === 5}`" :type="selectedEditOrder.discount_in_percentage === 5 ? 'indigo' : 'tertiary'" label="5%" size="xxs" />
                        <Button @click="selectedEditOrder.discount_in_percentage = 10" :key="`10${selectedEditOrder.discount_in_percentage === 10}`" :type="selectedEditOrder.discount_in_percentage === 10 ? 'indigo' : 'tertiary'" label="10%" size="xxs" />
                        <Button @click="selectedEditOrder.discount_in_percentage = 15" :key="`15${selectedEditOrder.discount_in_percentage === 15}`" :type="selectedEditOrder.discount_in_percentage === 15 ? 'indigo' : 'tertiary'" label="15%" size="xxs" />
                        <Button @click="selectedEditOrder.discount_in_percentage = 20" :key="`20${selectedEditOrder.discount_in_percentage === 20}`" :type="selectedEditOrder.discount_in_percentage === 20 ? 'indigo' : 'tertiary'" label="20%" size="xxs" />
                    </div>
                </div>
                
                <div class="flex justify-end gap-2">
                    <Button label="Batal" :style="'tertiary'" @click="isModalEditOrder = false"></Button>
                    <Button label="Ubah diskon" :disabled="!true" :loading="isLoadingEditOrder === selectedEditOrder?.id" full @click="onEditOrder()"></Button>
                </div>
            </div>
            <div v-else>
            </div>
        </Dialog>
    </section>
</template>